import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Stack,
  Chip,
  Typography,
  Tooltip,
  useTheme,
  Pagination,
} from '@mui/material';
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import FormSelect from '../forms/FormSelect';
import { useFormik } from 'formik';
import URLS from '../services/urlConstants';
import { get, put } from '../services/apiMethods';
import { LoadingButton } from '@mui/lab';
import PageHeader from '../components/PageHeader';
import CustomInput from '../components/SecondaryForms/CustomInput';
import { Delete, ExpandMore, Check, Close } from '@mui/icons-material';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
} from '@mui/material';
import StockPalletSummary from '../components/StockAuditDownload';
import TabList from '../components/Tabs/TabList';
import TabPanel from '../components/Tabs/TabPanel';
import AuditScanningTester from '../components/AuditScanningTester';
import ProfessionalPagination from '../components/ProfessionalPagination';

const TableCellStyled = styled(TableCell)({
  fontWeight: 'bold',
  border: '1px solid #ccc',
});

const yearData = [2025, 2026, 2027, 2028, 2029, 2030].map(year => ({
  label: year.toString(),
  value: year,
}));

const monthData = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
].map((month, index) => ({
  label: month,
  value: month,
}));

export default function NewAuditReports() {
  const theme = useTheme();
  const [warehouseData, setWarehouseData] = useState([]);
  const [palletCodeList, setPalletCodeList] = useState([]);
  const [filteredPalletCodeList, setFilteredPalletCodeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingView, setLoadingView] = useState(false);

  const [selectedPallets, setSelectedPallets] = useState({});
  const [expandedPallets, setExpandedPallets] = useState([]);
  const [inputValues, setInputValues] = useState({});
  const [scanTimeout, setScanTimeout] = useState(null);
  const scanTimeoutRef = useRef(null);
  const [palletSummary, setPalletSummary] = useState({});
  const [showChipData, setShowChipData] = useState(true);
  const [finalPalletSummary, setFinalPalletSummary] = useState(null);
  const [value, setValue] = useState(0);
  const [enableView, setEnableView] = useState(false);
  const [date, setDate] = useState([]);
  const [details, setDetails] = useState([]);
  const [editingMSN, setEditingMSN] = useState({ palletId: null, msn: null, newValue: '' });

  // Pagination state for pallets
  const [currentPage, setCurrentPage] = useState(1);
  const [palletsPerPage, setPalletsPerPage] = useState(5); // Show 5 pallets per page

  // Pagination state for submitted pallets
  const [submittedCurrentPage, setSubmittedCurrentPage] = useState(1);
  const [submittedPalletsPerPage, setSubmittedPalletsPerPage] = useState(5); // Show 5 submitted pallets per page

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
    };
  }, []);

  const handleTabChange = (e, newValue) => {
    setValue(newValue);
    entryFormik.resetForm();
  };

  const getWarehouseDetails = async () => {
    try {
      const { data } = await get(URLS.warehouse.list);
      setWarehouseData(
        data?.result.map(w => ({
          ...w,
          label: `${w.code}`,
          value: w._id,
        }))
      );
    } catch (error) {
      console.log(error);
    }
  };

  const getPalletCode = async () => {
    try {
      const { data } = await get(URLS.palletCode.list, {
        params: { search: { warehouse: formik.values.warehouse } },
      });

      setPalletCodeList(
        data?.result.map(w => ({
          ...w,
          label: w.code,
          value: w.code,
        }))
      );
      setFilteredPalletCodeList(
        data?.result.map(w => ({
          ...w,
          label: w.code,
          value: w.code,
        }))
      );
    } catch (error) {
      console.log(error);
    }
  };

  const getDate = async () => {
    try {
      const { data } = await get(URLS.auditReport.getDates, {
        params: {
          warehouseId: entryFormik.values.warehouseId,
          monthName: entryFormik.values.monthName,
          year: entryFormik.values.year,
        },
      });

      setDate(
        data?.result.map(w => ({
          ...w,
          label: w,
          value: w,
        }))
      );
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getWarehouseDetails();
  }, []);

  const handleDownloadReport = async values => {
    const payload = {};

    for (let key of Object.keys(palletSummary)) {
      payload[key] = palletSummary[key].totalItemsScanned;
    }

    try {
      setLoading(true);
      const { data } = await put(
        URLS.items.getFinalAuditReport + '/' + formik.values.warehouse,
        payload
      );

      setFinalPalletSummary(data.result);
      let newPalletSummary = { ...palletSummary, ...data.result.result };
      setPalletSummary(newPalletSummary);
      setEnableView(true);
      setLoading(false);
      window.localStorage.removeItem(formik.values.warehouse);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const getDetails = async () => {
    try {
      setLoadingView(true);
      const { data } = await get(URLS.auditReport.details, {
        params: {
          date: entryFormik.values.date,
          warehouseId: entryFormik.values.warehouseId,
        },
      });
      setDetails(data.result);

      setLoadingView(false);
    } catch (error) {
      console.log(error);
      setLoadingView(false);
    }
  };
  // 2025-06-02T18:30:00.000+00:00
  const formik = useFormik({
    initialValues: {
      warehouse: '',
      palletCode: '',
    },
    onSubmit: handleDownloadReport,
    enableReinitialize: true,
  });

  const entryFormik = useFormik({
    initialValues: {
      warehouseId: '',
      monthName: new Date().toLocaleString('default', { month: 'long' }),
      year: new Date().getFullYear(),
      date: '',
    },
    onSubmit: getDetails,
    enableReinitialize: true,
  });

  useEffect(() => {
    setFinalPalletSummary(null);
  }, [formik.values.warehouse]);

  useEffect(() => {
    if (formik.values.palletCode) {
      setSelectedPallets(prev => ({
        [formik.values.palletCode]: [],
      }));
    }
  }, [formik.values.palletCode]);

  useEffect(() => {
    if (formik.values.warehouse) {
      getPalletCode();
    }
  }, [formik.values.warehouse]);

  const handleInputChange = useCallback(
    (e, palletId) => {
      const { value } = e.target;

      // Update the input value immediately for visual feedback
      setInputValues(prev => ({
        ...prev,
        [palletId]: value,
      }));

      // Clear any existing timeout using ref to avoid stale closure issues
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }

      // Set a new timeout to process the complete scanned value
      scanTimeoutRef.current = setTimeout(() => {
        // Remove leading zeros and trim the value
        const formattedValue = value.replace(/^0+/, '').trim()?.toUpperCase();

        // If the value is not empty, add it to the selected pallets
        if (formattedValue) {
          setSelectedPallets(prev => {
            const currentValues = prev[palletId] || [];
            // Only add if it's not already in the list
            if (!currentValues.includes(formattedValue)) {
              return {
                ...prev,
                [palletId]: [...currentValues, formattedValue],
              };
            }
            return prev;
          });

          // Reset the input field after adding the MSN
          setInputValues(prev => ({
            ...prev,
            [palletId]: '',
          }));
        }

        // Clear the timeout ref after processing
        scanTimeoutRef.current = null;
      }, 100); // Increased to 100ms for better reliability with fast scanning
    },
    [] // Removed scanTimeout dependency to prevent unnecessary re-renders
  );

  const handleDeleteMSN = useCallback((palletId, msnToDelete) => {
    setSelectedPallets(prev => {
      const currentValues = prev[palletId] || [];
      return {
        ...prev,
        [palletId]: currentValues.filter(msn => msn !== msnToDelete),
      };
    });
  }, []);

  const handleRemovingPallet = async palletId => {
    try {
      const { data } = await put(URLS.items.checkPalletData + '/' + formik.values.warehouse, {
        [palletId]: selectedPallets[palletId],
      });

      let newPalletSummary = { ...palletSummary, ...data.result.result };

      // Save to localStorage with timestamp
      const cacheData = {
        data: newPalletSummary,
        timestamp: new Date().getTime(),
      };
      window.localStorage.setItem(formik.values.warehouse, JSON.stringify(cacheData));
      setPalletSummary(newPalletSummary);

      formik.setFieldValue('palletCode', '');
      setFilteredPalletCodeList(
        palletCodeList.filter(p => !Object.keys(newPalletSummary || {}).includes(p.value))
      );

      setSelectedPallets({});
      setInputValues({});
      setShowChipData(false);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const updatePalletSummary = async () => {
      if (formik.values.warehouse) {
        let prevData = window.localStorage.getItem(formik.values.warehouse);

        if (prevData) {
          try {
            let parsedData = JSON.parse(prevData);
            console.log('Parsed Data:', parsedData);

            if (
              parsedData.data &&
              typeof parsedData.data === 'object' &&
              !Array.isArray(parsedData.data)
            ) {
              setPalletSummary({ ...parsedData.data });
              setFilteredPalletCodeList(
                palletCodeList.filter(p => !Object.keys(parsedData.data || {}).includes(p.value))
              );
            } else {
              console.error('Invalid parsedData structure:', parsedData);
              setPalletSummary({});
            }
          } catch (error) {
            console.error('Error parsing localStorage data:', error);
            setPalletSummary({});
          }
        } else {
          setPalletSummary({});
        }
      }
    };

    updatePalletSummary();
  }, [formik.values.warehouse, palletCodeList, value]);

  useEffect(() => {
    if (formik.values.warehouse) {
      setExpandedPallets([]);
      let value = window.localStorage.getItem(formik.values.warehouse);

      if (value) {
        try {
          const parsedData = JSON.parse(value);
          setPalletSummary(parsedData.data || {});
        } catch (error) {
          console.error('Error parsing localStorage data:', error);
          setPalletSummary({});
        }
      } else {
        setPalletSummary({});
      }
    }
  }, [formik.values.warehouse]);

  const handleDeleteSubmittedPallet = palletId => {
    setPalletSummary(prev => {
      let newData = { ...prev };
      delete newData[palletId];
      return newData;
    });

    let newPalletSummary = { ...palletSummary };
    delete newPalletSummary[palletId];

    setFilteredPalletCodeList(
      palletCodeList.filter(p => !Object.keys(newPalletSummary || {}).includes(p.value))
    );
  };

  const toggleExpand = palletId => {
    setExpandedPallets(prev =>
      prev.includes(palletId) ? prev.filter(id => id !== palletId) : [...prev, palletId]
    );
  };

  useEffect(() => {
    if (entryFormik.values.warehouseId && entryFormik.values.monthName && entryFormik.values.year) {
      getDate();
    }
  }, [entryFormik.values.warehouseId, entryFormik.values.monthName, entryFormik.values.year]);

  const handleDeleteSummary = () => {
    setFinalPalletSummary(null);
    setDetails(null);
    setPalletSummary({});
    setEnableView(false);
  };

  const handleEditMSN = useCallback((palletId, msn) => {
    setEditingMSN({ palletId, msn, newValue: msn?.toUpperCase() });
  }, []);

  const handleSaveMSN = useCallback(
    (palletId, oldMSN) => {
      if (editingMSN.newValue.trim()) {
        setSelectedPallets(prev => {
          const currentValues = prev[palletId] || [];
          return {
            ...prev,
            [palletId]: currentValues.map(msn =>
              msn === oldMSN ? editingMSN.newValue.trim() : msn
            ),
          };
        });
      }
      setEditingMSN({ palletId: null, msn: null, newValue: '' });
    },
    [editingMSN.newValue]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingMSN({ palletId: null, msn: null, newValue: '' });
  }, []);

  // Memoized calculations for performance
  const totalMSNCount = useMemo(() => {
    return Object.values(selectedPallets).reduce((total, msns) => total + (msns?.length || 0), 0);
  }, [selectedPallets]);

  const palletList = useMemo(() => {
    return Object.keys(selectedPallets);
  }, [selectedPallets]);

  // Pagination calculations
  const paginationData = useMemo(() => {
    const totalPallets = palletList.length;
    const totalPages = Math.ceil(totalPallets / palletsPerPage);
    const startIndex = (currentPage - 1) * palletsPerPage;
    const endIndex = startIndex + palletsPerPage;
    const currentPallets = palletList.slice(startIndex, endIndex);

    return {
      totalPallets,
      totalPages,
      currentPallets,
      startIndex,
      endIndex: Math.min(endIndex, totalPallets),
    };
  }, [palletList, currentPage, palletsPerPage]);

  // Reset to first page when pallets change
  useEffect(() => {
    if (currentPage > paginationData.totalPages && paginationData.totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, paginationData.totalPages]);

  // Pagination calculations for submitted pallets
  const submittedPaginationData = useMemo(() => {
    const submittedPalletList = Object.keys(palletSummary || {});
    const totalSubmittedPallets = submittedPalletList.length;
    const totalPages = Math.ceil(totalSubmittedPallets / submittedPalletsPerPage);
    const startIndex = (submittedCurrentPage - 1) * submittedPalletsPerPage;
    const endIndex = startIndex + submittedPalletsPerPage;
    const currentSubmittedPallets = submittedPalletList.slice(startIndex, endIndex);

    return {
      totalSubmittedPallets,
      totalPages,
      currentSubmittedPallets,
      startIndex,
      endIndex: Math.min(endIndex, totalSubmittedPallets),
    };
  }, [palletSummary, submittedCurrentPage, submittedPalletsPerPage]);

  // Reset to first page when submitted pallets change
  useEffect(() => {
    if (
      submittedCurrentPage > submittedPaginationData.totalPages &&
      submittedPaginationData.totalPages > 0
    ) {
      setSubmittedCurrentPage(1);
    }
  }, [submittedCurrentPage, submittedPaginationData.totalPages]);

  // Memoized MSN renderer to prevent unnecessary re-renders
  const renderMSNs = useCallback(
    (pallet, msns) => {
      if (!msns || msns.length === 0) {
        return <Typography variant="body1">No MSNs scanned</Typography>;
      }

      const maxVisible = 50; // Performance optimization: limit visible MSNs
      const visibleMsns = msns.slice(0, maxVisible);
      const hasMore = msns.length > maxVisible;

      return (
        <>
          {visibleMsns.map((msn, index) =>
            editingMSN.palletId === pallet && editingMSN.msn === msn ? (
              <Box key={`${pallet}-${msn}-${index}`} sx={{ display: 'flex', gap: 1 }}>
                <CustomInput
                  size="small"
                  value={editingMSN.newValue}
                  onChange={e =>
                    setEditingMSN(prev => ({
                      ...prev,
                      newValue: e.target.value?.trim().toUpperCase(),
                    }))
                  }
                  label={`Edit MSN for ${pallet}`}
                  sx={{ width: '120px' }}
                />
                <IconButton size="small" color="primary" onClick={() => handleSaveMSN(pallet, msn)}>
                  <Check fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={handleCancelEdit}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Chip
                key={`${pallet}-${msn}-${index}`}
                label={msn}
                onDelete={() => handleDeleteMSN(pallet, msn)}
                onClick={() => handleEditMSN(pallet, msn)}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ cursor: 'pointer' }}
              />
            )
          )}
          {hasMore && (
            <Box
              sx={{
                width: '100%',
                mt: 1,
                p: 1,
                backgroundColor: 'warning.light',
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 'bold' }}>
                ⚡ Performance Mode: Showing first {maxVisible} of {msns.length} MSNs
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                {msns.length - maxVisible} more MSNs are hidden for optimal performance
              </Typography>
            </Box>
          )}
        </>
      );
    },
    [editingMSN, handleDeleteMSN, handleEditMSN, handleSaveMSN, handleCancelEdit]
  );

  return (
    <>
      <PageHeader
        title="Audit Reports"
        addButtonDisabled={true}
        showAdd={false}
        showAddField={false}
      />

      <TabList onChange={handleTabChange} value={value} labels={['Audit', 'View']} />

      <TabPanel index={0} value={value}>
        <Stack spacing={3}>
          <Paper
            sx={{
              width: '100%',
              maxWidth: '1200px',
              mx: 'auto',
              p: 2,
            }}
            component="form"
            onSubmit={formik.handleSubmit}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormSelect
                  options={warehouseData}
                  formik={formik}
                  name="warehouse"
                  label="Select Warehouse"
                  required={true}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormSelect
                  options={filteredPalletCodeList}
                  formik={formik}
                  name="palletCode"
                  label="Select Pallet Code"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <LoadingButton
                  fullWidth
                  loading={loading}
                  variant="contained"
                  size="small"
                  type="submit"
                  disabled={
                    !formik.values.warehouse || filteredPalletCodeList.length ? true : false
                  }
                >
                  Final Report
                </LoadingButton>
              </Grid>
            </Grid>
          </Paper>

          {/* Professional Pagination for Scanned Pallets */}
          {paginationData.totalPallets > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h6"
                color="primary"
                sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                📦 Scanned Pallets (Pending Submission)
              </Typography>
              <ProfessionalPagination
                totalItems={paginationData.totalPallets}
                itemsPerPage={palletsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={newItemsPerPage => {
                  setPalletsPerPage(newItemsPerPage);
                  setCurrentPage(1); // Reset to first page when changing items per page
                }}
                itemName="pallets"
                color="primary"
                showItemsPerPage={true}
                itemsPerPageOptions={[5, 10, 15, 20]}
              />
            </Box>
          )}

          {/* Render FormInputs for current page pallets */}
          {paginationData.currentPallets.map(pallet => (
            <Paper
              elevation={1}
              key={pallet}
              sx={{
                width: '100%',
                maxWidth: '1200px',
                mx: 'auto',
                p: 2,
                background: theme.palette.background.paper,
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={12}>
                  {/* Pallet header with count */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" color="primary">
                      Pallet: {pallet}
                    </Typography>
                    <Chip
                      label={`${selectedPallets[pallet]?.length || 0} MSNs Scanned`}
                      color={selectedPallets[pallet]?.length > 0 ? 'success' : 'default'}
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>

                  <CustomInput
                    name={`scanMSN-${pallet}`}
                    label={`Scan MSN for ${pallet}`}
                    required={true}
                    value={inputValues[pallet] || ''}
                    onChange={e => handleInputChange(e, pallet)}
                  />
                  {/* Display scanned MSNs as editable chips */}
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {renderMSNs(pallet, selectedPallets[pallet])}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={12} display={'flex'} justifyContent={'flex-end'}>
                  <Button
                    variant="contained"
                    onClick={() => handleRemovingPallet(pallet)}
                    size="small"
                  >
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          ))}

          {!finalPalletSummary ? null : (
            <StockPalletSummary
              data={finalPalletSummary}
              responseData={details}
              onDelete={handleDeleteSummary}
            />
          )}

          {/* Professional Pagination for Submitted Pallets */}
          {submittedPaginationData.totalSubmittedPallets > 0 && (
            <Box sx={{ mb: 2, mt: 3 }}>
              <Typography
                variant="h6"
                color="success.main"
                sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                ✅ Submitted Pallets (Awaiting Processing)
              </Typography>
              <ProfessionalPagination
                totalItems={submittedPaginationData.totalSubmittedPallets}
                itemsPerPage={submittedPalletsPerPage}
                currentPage={submittedCurrentPage}
                onPageChange={setSubmittedCurrentPage}
                onItemsPerPageChange={newItemsPerPage => {
                  setSubmittedPalletsPerPage(newItemsPerPage);
                  setSubmittedCurrentPage(1); // Reset to first page when changing items per page
                }}
                itemName="submitted pallets"
                color="success"
                showItemsPerPage={true}
                itemsPerPageOptions={[5, 10, 15, 20]}
              />
            </Box>
          )}

          {submittedPaginationData.currentSubmittedPallets.map(pallet => (
            <Paper
              elevation={1}
              key={pallet}
              sx={{
                width: '100%',
                maxWidth: '1200px',
                mx: 'auto',
                px: 2,
                py: 1,
                // boxShadow: "rgb(113, 138, 165) 0px 3px 6px -2px",
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={10} md={11}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Typography sx={{ color: '#196838' }}>
                      <strong>Pallet Code:</strong> {pallet}
                    </Typography>
                    <Chip
                      label={`${palletSummary[pallet]?.totalItemsScanned?.length || 0} MSNs`}
                      color="primary"
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  {showChipData && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {palletSummary[pallet]?.totalItemsScanned.map((msn, index) => (
                        <Chip
                          key={`${pallet}-${msn}-${index}`}
                          label={msn}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={2}
                  md={1}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'row',
                  }}
                >
                  {/* <Tooltip title="delete">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteSubmittedPallet(pallet)}
                    >
                      <Delete fontSize="small" color="error" />
                    </IconButton>
                  </Tooltip> */}

                  <Tooltip title="view">
                    <IconButton size="small" onClick={() => toggleExpand(pallet)}>
                      <ExpandMore fontSize="medium" color="primary" />
                    </IconButton>
                  </Tooltip>

                  {/* {enableView && (
                    <Tooltip title="view">
                      <IconButton
                        size="small"
                        onClick={() => toggleExpand(pallet)}
                      >
                        <ExpandMore fontSize="medium" color="primary" />
                      </IconButton>
                    </Tooltip>
                  )} */}
                </Grid>

                {expandedPallets.includes(pallet) && (
                  <Grid item xs={12}>
                    <Box sx={{ py: 1, borderTop: '1px solid #ccc' }}>
                      <Typography variant="body2">
                        Total Items As Per System :
                        <strong style={{ color: 'orange' }}>
                          {palletSummary[pallet]?.totalItemsAsPerSystem < 10
                            ? `0${palletSummary[pallet]?.totalItemsAsPerSystem}`
                            : palletSummary[pallet]?.totalItemsAsPerSystem}
                        </strong>
                      </Typography>
                      <Typography variant="body2">
                        Matched :{' '}
                        <strong style={{ color: 'orange' }}>
                          {' '}
                          {palletSummary[pallet]?.matched === true ? 'YES' : 'NO'}
                        </strong>
                      </Typography>
                      Total Items Scanned :{' '}
                      <strong style={{ color: 'orange' }}>
                        {palletSummary[pallet]?.totalItemsScanned?.length < 10
                          ? `0${palletSummary[pallet]?.totalItemsScanned?.length}`
                          : palletSummary[pallet]?.totalItemsScanned?.length}
                      </strong>{' '}
                      {palletSummary[pallet]?.totalItemsScanned.map((s, index) => (
                        <Chip
                          key={index}
                          label={s}
                          color="primary"
                          variant="outlined"
                          size="small"
                          sx={{ mr: '5px' }}
                        />
                      ))}
                      <br />
                      {/* Total Items Extra :{" "}
                      <strong style={{ color: "orange" }}>
                        {palletSummary[pallet]?.extraItems?.length < 10
                          ? `0${palletSummary[pallet]?.extraItems?.length}`
                          : palletSummary[pallet]?.extraItems?.length}
                      </strong>{" "} */}
                      {/* {palletSummary[pallet]?.extraItems.map((s, index) => (
                        <Chip
                          key={index}
                          label={s}
                          color="primary"
                          variant="outlined"
                          size="small"
                          sx={{ mr: '5px' }}
                        />
                      ))} */}
                      {/* <br /> */}
                      {!palletSummary[pallet]?.missingItems.length ? null : (
                        <>
                          <TableContainer
                            component={Paper}
                            sx={{ height: 400, overflow: 'scroll', mt: 1 }}
                          >
                            <Table aria-label="sticky table" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell colSpan={12} align="left">
                                    <Typography variant="body1" fontWeight="bold">
                                      Total Items Missing As Per Software :
                                    </Typography>{' '}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCellStyled align="center">
                                    <Typography fontWeight={600}> S.No</Typography>
                                  </TableCellStyled>
                                  <TableCellStyled align="center">
                                    <Typography fontWeight={600}>MSN</Typography>
                                  </TableCellStyled>
                                  <TableCellStyled align="center">
                                    <Typography fontWeight={600}>Item Description</Typography>
                                  </TableCellStyled>
                                </TableRow>
                              </TableHead>

                              <TableBody>
                                {palletSummary[pallet]?.missingItems?.map((row, index) => (
                                  <TableRow key={index}>
                                    <TableCellStyled align="center">{index + 1}</TableCellStyled>
                                    <TableCellStyled align="center">
                                      {row?.msn || 'N/A'}
                                    </TableCellStyled>
                                    <TableCellStyled align="center">
                                      {row?.itemDescription || 'N/A'}
                                    </TableCellStyled>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            {/* {palletSummary[pallet]?.mismatchedItems.length ==
                            0 && (
                            <Typography
                              variant="h6"
                              sx={{
                                textAlign: "center",
                                margin: "5px",

                                padding: "5px",
                              }}
                            >
                              No data found
                            </Typography>
                          )} */}
                          </TableContainer>
                        </>
                      )}
                      <br />
                      {!palletSummary[pallet]?.mismatchedItems.length ? null : (
                        <>
                          <strong>Mismatch of Pallets :</strong>{' '}
                          <TableContainer component={Paper}>
                            <Table>
                              <TableHead
                                sx={{
                                  backgroundColor: '#33669B',
                                }}
                              >
                                <TableRow>
                                  <TableCellStyled align="center">S.No</TableCellStyled>
                                  <TableCellStyled align="center">MSN</TableCellStyled>
                                  <TableCellStyled align="center">Pallet Code</TableCellStyled>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {palletSummary[pallet]?.mismatchedItems?.map((row, index) => (
                                  <TableRow key={index}>
                                    <TableCellStyled align="center">{index + 1}</TableCellStyled>
                                    <TableCellStyled align="center">
                                      {row?.msn || 'N/A'}
                                    </TableCellStyled>
                                    <TableCellStyled align="center">
                                      {row?.palletCode || 'N/A'}
                                    </TableCellStyled>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            {/* {palletSummary[pallet]?.mismatchedItems.length ===
                          0 ? (
                            <Typography
                              variant="h6"
                              sx={{
                                textAlign: "center",
                                margin: "5px",

                                padding: "5px",
                              }}
                            >
                              No data found
                            </Typography>
                          ) : null} */}
                          </TableContainer>
                        </>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          ))}
        </Stack>
      </TabPanel>
      <TabPanel index={1} value={value}>
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2} component="form" onSubmit={entryFormik.handleSubmit}>
            <Grid item xs={12} sm={6} md={3}>
              <FormSelect
                options={warehouseData}
                formik={entryFormik}
                name="warehouseId"
                label="Select Warehouse"
                required={true}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormSelect
                options={yearData}
                formik={entryFormik}
                name="year"
                label="Select Year"
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormSelect
                options={monthData}
                formik={entryFormik}
                name="monthName"
                label="Select Month"
                required={true}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormSelect
                options={date}
                formik={entryFormik}
                name="date"
                label="Select Date"
                required={true}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <LoadingButton
                loading={loadingView}
                variant="contained"
                size="small"
                disabled={
                  !entryFormik.values.warehouseId ||
                  !entryFormik.values.monthName ||
                  !entryFormik.values.year ||
                  !entryFormik.values.date
                }
                type="submit"
              >
                Submit
              </LoadingButton>
            </Grid>
          </Grid>
        </Paper>

        <StockPalletSummary
          showDelete={true}
          responseData={details}
          data={details?.reportData}
          onDelete={handleDeleteSummary}
        />

        {Object.keys(details?.reportData?.result || {}).map(pallet => {
          const palletData = details.reportData.result[pallet];

          return (
            <Paper
              elevation={1}
              key={pallet}
              sx={{
                width: '100%',
                maxWidth: '1200px',
                mx: 'auto',
                px: 2,
                py: 1,
                mt: 2,
                // boxShadow: "rgb(113, 138, 165) 0px 3px 6px -2px",
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={10} md={11}>
                  <Grid item xs={12} sx={{ color: '#196838' }}>
                    <strong>Pallet Code:</strong> {pallet}
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={2}
                  md={1}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'row',
                  }}
                >
                  <Tooltip title="view">
                    <IconButton size="small" onClick={() => toggleExpand(pallet)}>
                      <ExpandMore fontSize="medium" color="primary" />
                    </IconButton>
                  </Tooltip>
                </Grid>

                {expandedPallets.includes(pallet) && (
                  <Grid item xs={12}>
                    <Box sx={{ py: 1, borderTop: '1px solid #ccc' }}>
                      <Typography variant="body2">
                        Total Items As Per System :
                        <strong style={{ color: 'orange' }}>
                          {palletData?.totalItemsAsPerSystem < 10
                            ? `0${palletData?.totalItemsAsPerSystem}`
                            : palletData?.totalItemsAsPerSystem}
                        </strong>
                      </Typography>
                      <Typography variant="body2">
                        Matched :{' '}
                        <strong style={{ color: 'orange' }}>
                          {' '}
                          {palletData?.matched === true ? 'YES' : 'NO'}
                        </strong>
                      </Typography>
                      Total Items Scanned :{' '}
                      <strong style={{ color: 'orange' }}>
                        {palletData?.totalItemsScanned?.length < 10
                          ? `0${palletData?.totalItemsScanned?.length}`
                          : palletData?.totalItemsScanned?.length}
                      </strong>{' '}
                      {palletData?.totalItemsScanned.map((s, index) => (
                        <Chip
                          key={index}
                          label={s}
                          color="primary"
                          variant="outlined"
                          size="small"
                          sx={{ mr: '5px' }}
                        />
                      ))}
                      {/* <br />
                      Total Items Extra :{' '}
                      <strong style={{ color: 'orange' }}>
                        {palletData?.extraItems?.length < 10
                          ? `0${palletData?.extraItems?.length}`
                          : palletData?.extraItems?.length}
                      </strong>{' '}
                      {palletData?.extraItems.map((s, index) => (
                        <Chip
                          key={index}
                          label={s}
                          color="primary"
                          variant="outlined"
                          size="small"
                          sx={{ mr: '5px' }}
                        />
                      ))} */}
                      <br />
                      {!palletData?.missingItems.length ? null : (
                        <>
                          <TableContainer
                            component={Paper}
                            sx={{ height: 400, overflow: 'scroll', mt: 1 }}
                          >
                            <Table aria-label="sticky table" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell colSpan={12} align="left">
                                    <Typography variant="body1" fontWeight="bold">
                                      Total Items Missing As Per Software :
                                    </Typography>{' '}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCellStyled align="center">
                                    <Typography fontWeight={600}> S.No</Typography>
                                  </TableCellStyled>
                                  <TableCellStyled align="center">
                                    <Typography fontWeight={600}>MSN</Typography>
                                  </TableCellStyled>
                                  <TableCellStyled align="center">
                                    <Typography fontWeight={600}>Item Description</Typography>
                                  </TableCellStyled>
                                </TableRow>
                              </TableHead>

                              <TableBody>
                                {palletData?.missingItems?.map((row, index) => (
                                  <TableRow key={index}>
                                    <TableCellStyled align="center">{index + 1}</TableCellStyled>
                                    <TableCellStyled align="center">
                                      {row?.msn || 'N/A'}
                                    </TableCellStyled>
                                    <TableCellStyled align="center">
                                      {row?.itemDescription || 'N/A'}
                                    </TableCellStyled>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      )}
                      <br />
                      {!palletData?.mismatchedItems.length ? null : (
                        <>
                          <strong>Mismatch of Pallets :</strong>{' '}
                          <TableContainer component={Paper}>
                            <Table>
                              <TableHead
                                sx={{
                                  backgroundColor: '#33669B',
                                }}
                              >
                                <TableRow>
                                  <TableCellStyled align="center">S.No</TableCellStyled>
                                  <TableCellStyled align="center">MSN</TableCellStyled>
                                  <TableCellStyled align="center">Pallet Code</TableCellStyled>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {palletData?.mismatchedItems?.map((row, index) => (
                                  <TableRow key={index}>
                                    <TableCellStyled align="center">{index + 1}</TableCellStyled>
                                    <TableCellStyled align="center">
                                      {row?.msn || 'N/A'}
                                    </TableCellStyled>
                                    <TableCellStyled align="center">
                                      {row?.palletCode || 'N/A'}
                                    </TableCellStyled>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            {/* {palletSummary[pallet]?.mismatchedItems.length ===
                          0 ? (
                            <Typography
                              variant="h6"
                              sx={{
                                textAlign: "center",
                                margin: "5px",

                                padding: "5px",
                              }}
                            >
                              No data found
                            </Typography>
                          ) : null} */}
                          </TableContainer>
                        </>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          );
        })}

        {/* Bottom pagination for scanned pallets */}
        {paginationData.totalPallets > 0 && (
          <Box sx={{ mt: 3 }}>
            <ProfessionalPagination
              totalItems={paginationData.totalPallets}
              itemsPerPage={palletsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={newItemsPerPage => {
                setPalletsPerPage(newItemsPerPage);
                setCurrentPage(1);
              }}
              itemName="pallets"
              color="primary"
              showItemsPerPage={false} // Hide items per page selector at bottom
            />
          </Box>
        )}

        {/* Bottom pagination for submitted pallets */}
        {submittedPaginationData.totalSubmittedPallets > 0 && (
          <Box sx={{ mt: 3 }}>
            <ProfessionalPagination
              totalItems={submittedPaginationData.totalSubmittedPallets}
              itemsPerPage={submittedPalletsPerPage}
              currentPage={submittedCurrentPage}
              onPageChange={setSubmittedCurrentPage}
              onItemsPerPageChange={newItemsPerPage => {
                setSubmittedPalletsPerPage(newItemsPerPage);
                setSubmittedCurrentPage(1);
              }}
              itemName="submitted pallets"
              color="success"
              showItemsPerPage={false} // Hide items per page selector at bottom
            />
          </Box>
        )}

        {/* Performance indicator */}
        {totalMSNCount > 100 && (
          <Box sx={{ p: 2, backgroundColor: 'info.light', borderRadius: 1, mt: 2 }}>
            <Typography variant="body2" color="info.dark" sx={{ fontWeight: 'bold', mb: 1 }}>
              ⚡ Performance Mode Active
            </Typography>
            <Typography variant="body2" color="info.dark">
              Total: {totalMSNCount} MSNs across {palletList.length} pallet
              {palletList.length !== 1 ? 's' : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Large MSN lists are automatically optimized for better performance. Showing{' '}
              {palletsPerPage} pallets per page.
            </Typography>
          </Box>
        )}
      </TabPanel>

      <TabPanel index={2} value={value}>
        <AuditScanningTester
          onInputChange={handleInputChange}
          selectedPallets={selectedPallets}
          setSelectedPallets={setSelectedPallets}
        />
      </TabPanel>
    </>
  );
}

//
